import type { Module } from '@/lib/authz/catalogue'
import { useRights } from '@/hooks/use-rights'
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import SvgIcon from '@mui/material/SvgIcon'
import HomeSmileIcon from '@/icons/untitled-ui/duocolor/home-smile'
import Inbox01Icon from '@untitled-ui/icons-react/build/esm/Inbox01'
import { paths } from '@/paths'
import useTranslate from '@/hooks/use-translate'
import { User } from '@/api'
import { Mail01, Send01, Shuffle01, BarChart10, Settings01 } from '@untitled-ui/icons-react'
import { useAuth } from '@/hooks/use-auth'
import { Role } from '@/api'
import { FeatureNames } from '@/lib/featureFlags'
import { useFeatureFlags } from '@/hooks/use-feature-flags'

export interface Item {
  disabled?: boolean
  external?: boolean
  icon?: ReactNode
  items?: Item[]
  label?: ReactNode
  path?: string
  /** Si vrai : actif pour `path` et toute URL qui commence par `path/` (ex. /settings → /settings/account/...). */
  matchPathPrefix?: boolean
  title: string
  badgeLabel?: number
  hiddenIcon?: boolean
  /** Trait de séparation au-dessus de l'entrée. */
  dividerBefore?: boolean
}

export interface Section {
  items: Item[]
  subheader?: string
}

export const getSections = (
  user: User | null,
  tNav: (a: string) => string,
  isFeatureEnabled: (feature: FeatureNames) => boolean,
  /** En mode actif, les modules que le membre lit (TEC-1677) ; sinon tous. */
  canRead: (module: Module) => boolean = () => true
) => {
  const useNewTrackingView = isFeatureEnabled(FeatureNames.newTrackingView)

  const useSettingsFeature = isFeatureEnabled(FeatureNames.newSettings)

  const useReferentialsFeature = isFeatureEnabled(FeatureNames.referentials)

  const mainItems: Item[] = [
        {
          title: tNav('dashboard'),
          path: paths.dashboard.index,
          badgeLabel: undefined,
          icon: (
            <SvgIcon fontSize="small">
              <HomeSmileIcon />
            </SvgIcon>
          ),
        },
        ...(useNewTrackingView
          ? [
              {
                title: tNav('fieldList'),
                path: paths.dashboard.documents.list,
                badgeLabel: undefined,
                icon: (
                  <SvgIcon fontSize="small">
                    <Inbox01Icon />
                  </SvgIcon>
                ),
              },
            ]
          : [
              {
                title: tNav('fieldList'),
                path: paths.dashboard.documents.index,
                badgeLabel: undefined,
                icon: (
                  <SvgIcon fontSize="small">
                    <Inbox01Icon />
                  </SvgIcon>
                ),
                items: [
                  {
                    title: tNav('pendingField'),
                    path: paths.dashboard.documents.list,
                    badgeLabel: undefined,
                    hiddenIcon: true,
                    icon: (
                      <SvgIcon fontSize="small">
                        <Mail01 />
                      </SvgIcon>
                    ),
                  },
                  {
                    title: tNav('fieldExported'),
                    path: paths.dashboard.documents.exported,
                    badgeLabel: undefined,
                    icon: (
                      <SvgIcon fontSize="small">
                        <Send01 />
                      </SvgIcon>
                    ),
                  },
                ],
              },
            ]),

        {
          title: tNav('data'),
          path: paths.dashboard.customerFlow.index,
          badgeLabel: undefined,
          icon: (
            <SvgIcon fontSize="small">
              <Shuffle01 />
            </SvgIcon>
          ),
          items: [
            {
              title: tNav('models'),
              path: paths.dashboard.customerFlow.flow,
              matchPathPrefix: true,
              hiddenIcon: true,
            },
            {
              title: tNav('customers'),
              path: paths.dashboard.customerFlow.customer,
              matchPathPrefix: true,
              hiddenIcon: true,
            },
            ...(useReferentialsFeature
              ? [
                  {
                    title: tNav('referentials'),
                    path: paths.dashboard.customerFlow.referentials,
                    matchPathPrefix: true,
                    hiddenIcon: true,
                  },
                ]
              : []),
          ],
        },
      ]

  if (useSettingsFeature) {
    mainItems.push({
      title: tNav('settings'),
      path: paths.dashboard.settings.index,
      matchPathPrefix: true,
      dividerBefore: true,
      badgeLabel: undefined,
      icon: (
        <SvgIcon fontSize="small">
          <Settings01 />
        </SvgIcon>
      ),
    })
  }

  // Un module que le membre ne lit pas disparaît du menu (décision 16, TEC-1677), et un groupe vide avec lui.
  const readable = (item: Item): boolean => {
    switch (item.path) {
      case paths.dashboard.documents.index:
      case paths.dashboard.documents.list:
      case paths.dashboard.customerFlow.flow:
        return canRead('declarations')
      case paths.dashboard.customerFlow.customer:
      case paths.dashboard.customerFlow.referentials:
        return canRead('referentials')
      default:
        return true
    }
  }
  const visibleItems = mainItems
    .filter(readable)
    .map((item) => (item.items ? { ...item, items: item.items.filter(readable) } : item))
    .filter((item) => !item.items || item.items.length > 0)

  const sections: { subheader?: string; items: Item[] }[] = [
    {
      items: visibleItems,
    },
  ]

  if (
    !useSettingsFeature &&
    (user?.role === Role.Superadmin ||
      user?.role === Role.Admin ||
      user?.role === Role.Manager)
  ) {
    sections.push({
      items: [
        {
          title: tNav('managementConsole'),
          path: paths.dashboard.managementConsole.index,
          badgeLabel: undefined,
          icon: (
            <SvgIcon fontSize="small">
              <BarChart10 />
            </SvgIcon>
          ),
        },
      ],
    })
  }

  return sections
}

export const useSections = () => {
  const { t: tNav } = useTranslate('nav')
  const { user } = useAuth()
  const { isFeatureEnabled } = useFeatureFlags()
  const rights = useRights()

  return useMemo(
    () => getSections(user, tNav, isFeatureEnabled, rights.canRead),
    [tNav, isFeatureEnabled, rights]
  )
}
